import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import Footer from "common/Footer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import {
  Delete,
  Edit,
  Add as AddIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import { useSnackbar } from "../../context/SnackbarContext";
import { useCompany } from "../../context/CompanyContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

const CompanyManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [newCompanyData, setNewCompanyData] = useState({
    name: "",
    company_code: "",
    description: "",
  });
  const { setSelectedCompany: setContextSelectedCompany, selectedCompany: contextSelectedCompany } =
    useCompany();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const { t } = useLanguage();

  const fetchCompanies = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_URL}/api/companies/`);
      if (response.data.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      showSnackbar("Error fetching companies", "error");
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleSelectCompany = async (company) => {
    setContextSelectedCompany(company);
    showSnackbar(`Selected company: ${company.name}`, "success");
  };

  const handleDeleteCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/${selectedCompany.id}/delete/`);
      fetchCompanies();
      showSnackbar("Company deleted successfully", "success");
      setOpenDeleteModal(false);
    } catch (error) {
      showSnackbar("Error deleting company", "error");
    }
  };

  const handleAddCompany = async () => {
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/api/company/create/`, newCompanyData);
      fetchCompanies();
      showSnackbar("Company added successfully", "success");
      setOpenAddModal(false);
      setNewCompanyData({ name: "", company_code: "", description: "" });
    } catch (error) {
      showSnackbar("Error adding company", "error");
    }
  };

  const handleEditCompany = async () => {
    try {
      await api.put(`${process.env.REACT_APP_API_URL}/api/company/${selectedCompany.id}/update/`, {
        name: newCompanyData.name,
        company_code: newCompanyData.company_code,
        description: newCompanyData.description,
      });
      fetchCompanies();
      showSnackbar("Company updated successfully", "success");
      setOpenEditModal(false);
    } catch (error) {
      showSnackbar("Error updating company", "error");
    }
  };

  const handleOpenAddModal = () => {
    setNewCompanyData({ name: "", company_code: "", description: "" });
    setOpenAddModal(true);
  };

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" color="white">
                    {t("companyManagement")}
                  </MDTypography>
                  <MDButton
                    variant="contained"
                    color="white"
                    onClick={handleOpenAddModal}
                    startIcon={<AddIcon />}
                    sx={{
                      boxShadow: 1,
                      "&:hover": {
                        boxShadow: 2,
                      },
                    }}
                  >
                    {t("addCompany")}
                  </MDButton>
                </MDBox>
              </MDBox>

              <MDBox p={3}>
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                    boxShadow: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#f8f9fa" }}>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("companyName")}</th>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("companyCode")}</th>
                          <th style={{ padding: "16px", textAlign: "left" }}>{t("description")}</th>
                          <th style={{ padding: "16px", textAlign: "center" }}>{t("status")}</th>
                          <th style={{ padding: "16px", textAlign: "center" }}>{t("actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company) => (
                          <tr key={company.id}>
                            <td style={{ padding: "16px" }}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <BusinessIcon color="info" />
                                <Typography variant="body2">{company.name}</Typography>
                              </Box>
                            </td>
                            <td style={{ padding: "16px" }}>
                              <Typography variant="body2">{company.company_code}</Typography>
                            </td>
                            <td style={{ padding: "16px" }}>
                              <Typography variant="body2" color="text.secondary">
                                {company.description || "-"}
                              </Typography>
                            </td>
                            <td style={{ padding: "16px", textAlign: "center" }}>
                              {contextSelectedCompany?.id === company.id ? (
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label={t("selected")}
                                  color="success"
                                  size="small"
                                  sx={{ minWidth: 100 }}
                                />
                              ) : (
                                <Chip
                                  label={t("notSelected")}
                                  color="default"
                                  size="small"
                                  sx={{ minWidth: 100 }}
                                />
                              )}
                            </td>
                            <td style={{ padding: "16px", textAlign: "center" }}>
                              <Box display="flex" justifyContent="center" gap={1}>
                                <Box
                                  sx={{
                                    minWidth: 120,
                                    visibility:
                                      contextSelectedCompany?.id === company.id
                                        ? "hidden"
                                        : "visible",
                                  }}
                                >
                                  <MDButton
                                    onClick={() => handleSelectCompany(company)}
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    fullWidth
                                  >
                                    {t("select")}
                                  </MDButton>
                                </Box>
                                <Tooltip title={t("editCompany")}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedCompany(company);
                                      setNewCompanyData({
                                        name: company.name,
                                        company_code: company.company_code,
                                        description: company.description || "",
                                      });
                                      setOpenEditModal(true);
                                    }}
                                    color="primary"
                                    size="small"
                                  >
                                    <Edit />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t("deleteCompany")}>
                                  <IconButton
                                    onClick={() => {
                                      setSelectedCompany(company);
                                      setOpenDeleteModal(true);
                                    }}
                                    color="error"
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Add Company Modal */}
      <Dialog open={openAddModal} onClose={() => setOpenAddModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("addNewCompany")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              autoFocus
              label={t("companyName")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("companyCode")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.company_code}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, company_code: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("description")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.description}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenAddModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleAddCompany}>
            {t("addCompany")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Edit Company Modal */}
      <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("editCompany")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              autoFocus
              label={t("companyName")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.name}
              onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("companyCode")}
              type="text"
              fullWidth
              variant="outlined"
              value={newCompanyData.company_code}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, company_code: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("description")}
              type="text"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={newCompanyData.description}
              onChange={(e) =>
                setNewCompanyData({ ...newCompanyData, description: e.target.value })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenEditModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="info" onClick={handleEditCompany}>
            {t("saveChanges")}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("deleteConfirmation")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("areYouSure")} {selectedCompany?.name}? {t("thisActionCannotBeUndone")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setOpenDeleteModal(false)}>
            {t("cancel")}
          </MDButton>
          <MDButton variant="contained" color="error" onClick={handleDeleteCompany}>
            {t("delete")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default CompanyManagement;
